/* REPLACE ENTIRE FILE WITH THIS VERSION */
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

/* ---------------- helpers ---------------- */
const currency = (n, c = "EUR") => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "€ 0.00";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: c,
    }).format(v);
  } catch {
    return `€ ${v.toFixed(2)}`;
  }
};

function formatDate(value) {
  if (!value) return "-";
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split("-");
    return `${d}-${m}-${y}`;
  }
  const dt = new Date(s);
  if (!isNaN(dt)) {
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yyyy = dt.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  return s;
}

function StatusBadge({ status }) {
  const label = (status || "draft").toLowerCase();
  const map = {
    accepted: { bg: "#dcfce7", fg: "#15803d", br: "#bbf7d0" },
    sent: { bg: "#e0f2fe", fg: "#075985", br: "#bae6fd" },
    rejected: { bg: "#fee2e2", fg: "#991b1b", br: "#fecaca" },
    expired: { bg: "#f3f4f6", fg: "#374151", br: "#e5e7eb" },
    draft: { bg: "#f3f4f6", fg: "#374151", br: "#e5e7eb" },
  };
  const { bg, fg, br } = map[label] || map.draft;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 8px",
        borderRadius: 999,
        fontSize: 12,
        background: `var(--badge-${label}-bg, ${bg})`,
        color: `var(--badge-${label}-fg, ${fg})`,
        border: `1px solid var(--badge-${label}-br, ${br})`,
        textTransform: "lowercase",
      }}
    >
      {label}
    </span>
  );
}

// --- Email templates (backend-first, localStorage fallback) ---
const LS_KEYS = { quote: "jwcs.emailTemplate.quote" };
const DEFAULTS = {
  quote: `Hello,

Please find attached your quotation {{number}}.

Kind regards,`,
};

async function loadEmailTemplate(type) {
  try {
    const res = await fetch(`/api/email-templates?type=${encodeURIComponent(type)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return (json && json.message) || DEFAULTS[type];
  } catch {
    return localStorage.getItem(LS_KEYS[type]) || DEFAULTS[type];
  }
}

function mergePlaceholders(template, ctx) {
  return String(template || "")
    .replace(/\{\{number\}\}/g, ctx.number || "")
    .replace(/\{\{customer\}\}/g, ctx.customer || "")
    .replace(/\{\{company\}\}/g, ctx.company || "");
}

// GET /api/quotes?... -> { data, total }
async function fetchQuotes(params = {}) {
  const { q = "", status = "all", from, to, page = 1, pageSize = 20 } = params;
  const usp = new URLSearchParams();
  if (q) usp.set("q", q);
  if (status && status !== "all") usp.set("status", status);
  if (from) usp.set("from", from);
  if (to) usp.set("to", to);
  usp.set("page", String(page));
  usp.set("pageSize", String(pageSize));

  const res = await fetch(`/api/quotes?${usp.toString()}`);
  if (!res.ok)
    throw new Error((await res.text().catch(() => "")) || `HTTP ${res.status}`);
  const json = await res.json();

  const data = (json.data || []).map((row) => ({
    id: String(row.id),
    number: row.quote_number,
    customerName:
      `${row.first_name ?? ""} ${row.surname ?? ""}`.trim() ||
      `Customer #${row.customer_id}`,
    createdAt: row.created_at,
    total: Number(row.total),
    currency: "EUR",
    status: (row.status || (row.approved ? "accepted" : "draft")).toLowerCase(),
    pdfUrl: `/api/quotes/${row.id}/pdf`,
  }));

  return { data, total: Number(json.total || data.length) };
}

/* ---------------- component ---------------- */
export default function QuotesPage() {
  const navigate = useNavigate();

  // filters / paging
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // selection
  const [selected, setSelected] = useState(() => new Set());
  const selectedIds = useMemo(() => Array.from(selected), [selected]);
  const selectedCount = selectedIds.length;
  const canSingleAct = selectedCount === 1;

  // pdf modal
  const [open, setOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(undefined);
  const [pdfName, setPdfName] = useState("quote.pdf");
  const [numPages, setNumPages] = useState(null);
  const [pdfErr, setPdfErr] = useState("");

  // responsive PDF width
  const pdfContainerRef = useRef(null);
  const [pdfWidth, setPdfWidth] = useState(900);

  // email state
  const [currentQuote, setCurrentQuote] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailMsg, setEmailMsg] = useState("");

  // delete state
  const [deletingId, setDeletingId] = useState(null);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / pageSize)),
    [total, pageSize]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await fetchQuotes({ q, status, from, to, page, pageSize });
      setRows(res.data);
      setTotal(res.total);
      // trim selection to visible
      setSelected((prev) => {
        const next = new Set(prev);
        const visible = new Set(res.data.map((r) => r.id));
        for (const id of Array.from(next)) if (!visible.has(id)) next.delete(id);
        return next;
      });
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [q, status, from, to, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  // resize PDF pages to fit modal width
  useEffect(() => {
    if (!open) return;

    function updateWidth() {
      if (pdfContainerRef.current) {
        const w = pdfContainerRef.current.clientWidth;
        setPdfWidth(w > 0 ? w : 900);
      }
    }

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [open]);

  // selection helpers
  const allOnPageSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someOnPageSelected =
    rows.some((r) => selected.has(r.id)) && !allOnPageSelected;

  function toggleSelect(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllOnPage(checked) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of rows) {
        if (checked) next.add(r.id);
        else next.delete(r.id);
      }
      return next;
    });
  }

  // PDF helpers
  function onPdfLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  async function openPdf(url, name = "quote.pdf") {
    if (!url) return;
    setOpen(true);
    setPdfErr("");
    setNumPages(null);
    setPdfName(name);

    if (pdfUrl && String(pdfUrl).startsWith("blob:")) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(undefined);
    }

    try {
      const res = await fetch(url);
      if (!res.ok)
        throw new Error(
          (await res.text().catch(() => "")) || `HTTP ${res.status}`
        );
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      setPdfUrl(objUrl);
    } catch (e) {
      setPdfErr(String(e.message || e));
    }
  }

  function downloadCurrentPdf() {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = pdfName || "quote.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  // ----- EMAIL: now pulls template & merges placeholders -----
  async function sendEmailForQuote(row) {
    if (!row) return;
    setEmailSending(true);
    setEmailMsg("");
    try {
      const customer = row.customerName || "";
      const company = document
        ?.querySelector('meta[name="app-company"]')
        ?.getAttribute("content") || ""; // optional, if you add a meta tag

      const template = await loadEmailTemplate("quote");
      const message = mergePlaceholders(template, {
        number: row.number,
        customer,
        company,
      });

      const res = await fetch(`/api/quotes/${row.id}/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: `Quotation ${row.number}`,
          message,
        }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }

      // refresh to reflect "sent" status if backend updates it
      await load();

      const successMsg = "Email sent ✓";
      if (open) {
        setEmailMsg(successMsg);
      } else {
        alert(successMsg);
      }
    } catch (e) {
      const failMsg = `Failed to send: ${e.message || e}`;
      if (open) {
        setEmailMsg(failMsg);
      } else {
        alert(failMsg);
      }
    } finally {
      setEmailSending(false);
    }
  }

  // email used from inside modal
  async function emailCurrentQuote() {
    if (!currentQuote) return;
    await sendEmailForQuote(currentQuote);
  }

  // delete quote
  async function deleteQuote(row) {
    if (!row) return;
    if (
      !window.confirm(
        `Delete quote ${row.number || row.id}? This cannot be undone.`
      )
    ) {
      return;
    }
    setDeletingId(row.id);
    try {
      let res = await fetch(`/api/quotes/${row.id}`, { method: "DELETE" });

      if (!res.ok && (res.status === 404 || res.status === 405)) {
        res = await fetch(`/api/quotes/delete?id=${row.id}`, { method: "POST" });
      }

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          text ||
            `Delete endpoint not implemented (HTTP ${res.status}).`
        );
      }

      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setTotal((t) => Math.max(0, (t || 0) - 1));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    } catch (e) {
      alert(`Failed to delete: ${e.message || e}`);
    } finally {
      setDeletingId(null);
    }
  }

  // top actions
  function pickOne() {
    if (!canSingleAct) return null;
    const id = selectedIds[0];
    return rows.find((r) => r.id === id) || null;
  }

  function handleTopView() {
    const row = pickOne();
    if (row) {
      setCurrentQuote(row);
      openPdf(row.pdfUrl, `quote-${row.number || row.id}.pdf`);
    }
  }

  function handleTopEdit() {
    const row = pickOne();
    if (row) navigate(`/quote-builder?id=${row.id}`);
  }

  async function handleTopEmail() {
    const row = pickOne();
    if (!row) return;
    setCurrentQuote(row);
    await sendEmailForQuote(row);
  }

  function handleTopBook() {
    const row = pickOne();
    if (!row) return;
    const params = new URLSearchParams({
      quoteId: row.id,
      quoteNumber: row.number || "",
      name: row.customerName || "",
    });
    navigate(`/calendar?${params.toString()}`);
  }

  function handleTopDelete() {
    const row = pickOne();
    if (row) deleteQuote(row);
  }

  return (
    <div className="card">
      {/* Styles for layout + mobile top bar + table headers */}
      <style>{`
        .q-topbar {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          align-items: center;
          margin-bottom: 12px;
        }
        .q-filters {
          margin: 12px 0;
          border: 1px solid var(--border);
          background: var(--card);
          border-radius: 12px;
        }
        .q-filters__header {
          padding: 10px 12px;
          border-bottom: 1px solid var(--border);
          font-weight: 600;
        }
        .q-filters__body { padding: 12px; }
        .q-grid {
          display: grid;
          gap: 10px;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        }
        .q-table { width: 100%; border-collapse: collapse; }
        .q-table th, .q-table td {
          padding: 8px 10px;
          border-bottom: 1px solid var(--border);
        }
        .q-table thead th { text-align: left; }

        /* Desktop / tablet column sizing */
        .q-col-select { width: 44px; }
        .q-col-number { width: 160px; }
        .q-col-customer { }
        .q-col-created { width: 160px; }
        .q-col-total { width: 140px; text-align: right; }
        .q-col-status { width: 140px; }

        .q-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
          margin-top: 10px;
          flex-wrap: nowrap;
        }

        .q-pagination__controls {
          display: flex;
          flex-wrap: nowrap;
          align-items: center;
          justify-content: flex-end;
          gap: 6px;
        }

        .q-pagination__controls .btn {
          padding: 2px 10px;
          font-size: 12px;
          line-height: 1.2;
          width: auto;
          flex: 0 0 auto;
          white-space: nowrap;
        }

        .q-pagination__page {
          min-width: auto;
          font-size: 12px;
          text-align: center;
          white-space: nowrap;
        }

        .muted { opacity: .8; }

        @media (max-width: 719.98px) {
          .q-topbar {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
            align-items: stretch;
          }
          .q-topbar > .btn { width: 100%; }

          .q-topbar > .btn:nth-of-type(1) { grid-column: 1; }
          .q-topbar > .btn:nth-of-type(2) { grid-column: 2; }
          .q-topbar > .btn:nth-of-type(3) { grid-column: 1; }
          .q-topbar > .btn:nth-of-type(4) { grid-column: 2; }
          .q-topbar > .btn:nth-of-type(5) { grid-column: 1 / -1; }

          .q-topbar > .muted {
            grid-column: 1 / -1;
            justify-self: end;
            margin-left: 0 !important;
          }

          .q-table thead th {
            font-size: 11px;
            padding: 6px 4px;
            white-space: nowrap;
          }

          .q-pagination {
            flex-direction: column;
            align-items: stretch;
            gap: 4px;
          }
          .q-pagination__controls { justify-content: center; }

          .q-col-select,
          .q-col-number,
          .q-col-created,
          .q-col-total,
          .q-col-status { width: auto; }
        }
      `}</style>

      <h2>Quotes</h2>

      {/* Top actions */}
      <div className="q-topbar">
        <button className="btn" onClick={handleTopView} disabled={!canSingleAct}>
          View
        </button>
        <button className="btn" onClick={handleTopEdit} disabled={!canSingleAct}>
          Edit
        </button>
        <button
          className="btn"
          onClick={handleTopEmail}
          disabled={!canSingleAct || emailSending}
        >
          {emailSending ? "Sending…" : "Email"}
        </button>
        <button
          className="btn"
          onClick={handleTopBook}
          disabled={!canSingleAct}
        >
          Book Job
        </button>
        <button
          className="btn"
          onClick={handleTopDelete}
          disabled={!canSingleAct || deletingId === selectedIds[0]}
          style={{
            background: "#991b1b",
            border: "1px solid #991b1b",
            color: "#fee2e2",
          }}
          title={
            canSingleAct
              ? "Delete selected quote"
              : "Select exactly one quote"
          }
        >
          {deletingId === selectedIds[0] ? "Deleting…" : "Delete"}
        </button>

        <div className="muted" style={{ marginLeft: "auto" }}>
          {selectedCount > 0 ? `${selectedCount} selected` : "No quotes selected"}
        </div>
      </div>

      {/* Error banner */}
      {err && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            borderRadius: 8,
            border: "1px solid #fecaca",
            background: "#fee2e2",
            color: "#991b1b",
          }}
        >
          {err}
        </div>
      )}

      {/* Filters card */}
      <div className="q-filters">
        <div className="q-filters__header">Filters</div>
        <div className="q-filters__body">
          <div className="q-grid">
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="label">Search</label>
              <input
                className="input"
                placeholder="Quote # or customer…"
                value={q}
                onChange={(e) => {
                  setPage(1);
                  setQ(e.target.value);
                }}
              />
            </div>
            <div>
              <label className="label">Status</label>
              <select
                className="input"
                value={status}
                onChange={(e) => {
                  setPage(1);
                  setStatus(e.target.value);
                }}
              >
                <option value="all">All</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            <div>
              <label className="label">From</label>
              <input
                className="input"
                type="date"
                value={from}
                onChange={(e) => {
                  setPage(1);
                  setFrom(e.target.value);
                }}
              />
            </div>
            <div>
              <label className="label">To</label>
              <input
                className="input"
                type="date"
                value={to}
                onChange={(e) => {
                  setPage(1);
                  setTo(e.target.value);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <table className="q-table table">
        <thead>
          <tr>
            <th className="q-col-select">
              <input
                type="checkbox"
                aria-label="Select all on page"
                checked={allOnPageSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someOnPageSelected;
                }}
                onChange={(e) => selectAllOnPage(e.target.checked)}
              />
            </th>
            <th className="q-col-number">Quote #</th>
            <th className="q-col-customer">Customer</th>
            <th className="q-col-created">Created</th>
            <th className="q-col-total">Total</th>
            <th className="q-col-status">Status</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td
                colSpan={6}
                className="muted"
                style={{ padding: 16, textAlign: "center" }}
              >
                Loading…
              </td>
            </tr>
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td
                colSpan={6}
                className="muted"
                style={{ padding: 16, textAlign: "center" }}
              >
                No quotes found.
              </td>
            </tr>
          )}
          {!loading &&
            rows.map((r) => (
              <tr key={r.id}>
                <td className="q-col-select">
                  <input
                    type="checkbox"
                    aria-label={`Select quote ${r.number || r.id}`}
                    checked={selected.has(r.id)}
                    onChange={() => toggleSelect(r.id)}
                  />
                </td>
                <td className="q-col-number" style={{ fontWeight: 600 }}>
                  #{r.number}
                </td>
                <td className="q-col-customer">{r.customerName}</td>
                <td className="q-col-created">{formatDate(r.createdAt)}</td>
                <td className="q-col-total" style={{ textAlign: "right" }}>
                  {currency(r.total, r.currency)}
                </td>
                <td className="q-col-status">
                  <StatusBadge status={r.status} />
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="q-pagination">
        <div className="muted q-pagination__summary">
          {(total || 0).toLocaleString()} result{total === 1 ? "" : "s"}
        </div>
        <div className="q-pagination__controls">
          <button
            className="btn secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>
          <div className="muted q-pagination__page">
            Page {page} / {totalPages}
          </div>
          <button
            className="btn secondary"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>

      {/* PDF Modal */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(1000px, 96vw)",
              maxHeight: "90vh",
              background: "var(--card)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              borderRadius: 12,
              boxShadow: "var(--shadow)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid var(--border)",
                fontWeight: 600,
              }}
            >
              Quote PDF Preview
            </div>

            <div
              ref={pdfContainerRef}
              style={{
                padding: 16,
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {pdfErr ? (
                <div
                  style={{
                    padding: 16,
                    borderRadius: 8,
                    border: "1px solid #fecaca",
                    background: "#fee2e2",
                    color: "#991b1b",
                  }}
                >
                  Failed to load PDF: {pdfErr}
                </div>
              ) : !pdfUrl ? (
                <div
                  className="muted"
                  style={{ padding: 24, textAlign: "center" }}
                >
                  Loading PDF…
                </div>
              ) : (
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onPdfLoadSuccess}
                  onLoadError={(e) => setPdfErr(String(e?.message || e))}
                  onSourceError={(e) => setPdfErr(String(e?.message || e))}
                  loading={
                    <div
                      className="muted"
                      style={{ padding: 24, textAlign: "center" }}
                    >
                      Loading PDF…
                    </div>
                  }
                >
                  {Array.from(new Array(numPages || 0), (_, idx) => (
                    <Page
                      key={`p_${idx + 1}`}
                      pageNumber={idx + 1}
                      width={pdfWidth}
                    />
                  ))}
                </Document>
              )}
            </div>

            <div
              style={{
                padding: 12,
                borderTop: "1px solid var(--border)",
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {pdfUrl && (
                <>
                  <button className="btn" onClick={downloadCurrentPdf}>
                    Download
                  </button>
                  <button
                    className="btn"
                    onClick={emailCurrentQuote}
                    disabled={emailSending}
                    title={
                      currentQuote
                        ? `Send to ${currentQuote.customerName}`
                        : "No quote selected"
                    }
                  >
                    {emailSending ? "Sending…" : "Email to Customer"}
                  </button>
                  {emailMsg && (
                    <span
                      style={{
                        fontSize: 12,
                        color: emailMsg.startsWith("Email sent")
                          ? "#15803d"
                          : "#b91c1c",
                        marginRight: "auto",
                      }}
                    >
                      {emailMsg}
                    </span>
                  )}
                </>
              )}
              <button className="btn secondary" onClick={() => setOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
